import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { updateSegment, setDepartureTime } from '../../redux/currentSearch/actions';
import {
    isTrainsProductActive,
    getTripType,
    getSegmentById,
} from '../../redux/currentSearch/selectors';
import { ABUtils, DateUtils, TimeUtils, SessionStorageUtils } from '..';
import { TripType } from '../../models';
import { deriveInitialTime } from './useTimeConstraints';
import { MINUTE_SLOTS } from '../../widgets/FlightsManager/MobileFields/TimePicker/constants';

export function roundToNearestSlot(timeStr, minuteSlots = MINUTE_SLOTS) {
    if (timeStr == null) return null;

    const time = String(timeStr);

    if (!/^\d{4}$/.test(time)) return null;

    const hour = time.slice(0, 2);
    const minutes = Number(time.slice(2, 4));

    let closestSlot = minuteSlots[0];
    let smallestDiff = Math.abs(Number(closestSlot) - minutes);

    for (let i = 1; i < minuteSlots.length; i++) {
        const slot = minuteSlots[i];
        const diff = Math.abs(Number(slot) - minutes);

        if (diff < smallestDiff) {
            smallestDiff = diff;
            closestSlot = slot;
        }
    }

    return `${hour}${closestSlot}`;
}

export const hasValidDepartureTime = (departureTime) => {
    if (departureTime == null) return false;

    const timeStr = String(departureTime);

    if (!/^\d{4}$/.test(timeStr)) {
        return false;
    }

    const hour = Number(timeStr.slice(0, 2));
    const minute = Number(timeStr.slice(2, 4));

    const isValidHour = hour >= 0 && hour <= 23;
    const isValidMinute = minute >= 0 && minute <= 59;

    return isValidHour && isValidMinute;
};


export const parseSessionDepartureParams = () => {
    try {
        const searchParameters = SessionStorageUtils.getValue('searchParameters');
        if (!searchParameters) {
            return null;
        }

        const parsedParams = JSON.parse(searchParameters);

        const oneWayTrainSegment = parsedParams.searchSegments?.[0];

        const departureTime = parsedParams.depTime;
        const departureDate = oneWayTrainSegment?.dateStr;

        return {
            depTime: departureTime != null ? String(departureTime) : null,
            date: departureDate ?? null,
        };
    } catch {
        return null;
    }
};

export const getSessionDepartureTime = (session) => {
    if (!session?.depTime || !hasValidDepartureTime(session.depTime)) return null;

    return roundToNearestSlot(session.depTime, MINUTE_SLOTS);
};

export const getDefaultDepartureTime = (date) => {
    const parsedDate = date ? DateUtils.stringToDate(date) : null;

    const currentTime =
        !parsedDate || DateUtils.isToday(parsedDate)
            ? TimeUtils.getCurrentTime()
            : null;

    const { hour, minute } = deriveInitialTime(currentTime, MINUTE_SLOTS);

    return `${hour}${minute}`;
};

export const useOneWayDatePreselection = (segmentId, date) => {
    const dispatch = useDispatch();

    const isTrainsProduct = useSelector(isTrainsProductActive);
    const isOneWay = useSelector((state) => getTripType(state) === TripType.ONE_WAY);
    const departureTime = useSelector((state) =>
        getSegmentById(state, { segmentId })?.departureTime,
    );
    const hasPreselected = useRef(false);

    useEffect(() => {
        const isSearchByHourMobile = ABUtils.getABs().isSearchByHourMobile && isTrainsProduct;

        if (!isSearchByHourMobile || !isOneWay) {
            hasPreselected.current = false;
            return;
        }
        if (hasPreselected.current) return;

        // 1. Already set in Redux
        if (hasValidDepartureTime(departureTime)) {
            hasPreselected.current = true;
            return;
        }

        // 2. Session
        const session = parseSessionDepartureParams();

        const sessionTime = getSessionDepartureTime(session);

        // 3. Fallback
        const timeToSet = sessionTime ?? getDefaultDepartureTime(date);

        if (date) {
            dispatch(setDepartureTime(segmentId, timeToSet));
        } else {
            const dateToSet = session?.date ? DateUtils.stringToDate(session.date) : DateUtils.getToday();

            batch(() => {
                dispatch(updateSegment({ date: dateToSet }, segmentId));
                dispatch(setDepartureTime(segmentId, timeToSet));
            });
        }

        hasPreselected.current = true;
    }, [dispatch, segmentId, isTrainsProduct, isOneWay, date, departureTime]);
};
